import { useCallback, useEffect, useRef, useState } from 'react';
import { printService } from 'api/services/printService';
import type { PrintAlerts } from 'api/types';

const POLL_MS = 15_000;

/**
 * Print trouble for one outlet (BR-PRN-002: a print failure is never silent),
 * polled every 15 s like the rest of DineOps. Unresolved failed / rerouted jobs
 * and offline agents. A failed poll keeps the last answer: the banner must not
 * flicker away because one request timed out.
 *
 * Pass enabled=false (e.g. a role without the print screens) to skip polling.
 */
export const usePrintAlerts = (scopeId?: string | null, enabled = true) => {
  const [alerts, setAlerts] = useState<PrintAlerts | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const alive = useRef(true);

  const refetch = useCallback(async () => {
    try {
      const a = await printService.getAlerts(scopeId);
      if (alive.current) { setAlerts(a); setError(null); }
    } catch (err) {
      if (alive.current) setError(err as Error);
    }
  }, [scopeId]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };
    void refetch();
    const t = window.setInterval(() => { void refetch(); }, POLL_MS);
    return () => { alive.current = false; window.clearInterval(t); };
  }, [refetch, enabled]);

  return { alerts, error, refetch };
};

export default usePrintAlerts;
